import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { canAccess } from "../hooks/usePermission";
import "./Sidebar.css";

/* ---------- Small icons ---------- */

const SearchIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const BackIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19 12H5" />
    <path d="m11 6-6 6 6 6" />
  </svg>
);

const PlusIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

const WalletIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 7.5A2.5 2.5 0 0 1 6.5 5H18a1 1 0 0 1 1 1v2" />
    <path d="M4 7.5V17a2 2 0 0 0 2 2h12a1 1 0 0 0 1-1v-9a1 1 0 0 0-1-1H6.5A2.5 2.5 0 0 1 4 7.5z" />
    <circle cx="15.5" cy="13.5" r="1.1" />
  </svg>
);

const ReportIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 3h9l4 4v14H6z" />
    <path d="M15 3v4h4" />
    <path d="M9 17v-3" />
    <path d="M12 17v-6" />
    <path d="M15 17v-4" />
  </svg>
);

const Sidebar = ({
  projects,
  onSelectProject,
  onAddProject,
  filterRoute,
  selectedProjectId,
}) => {
  const [newProject, setNewProject] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const pathname = location.pathname;
  let currentFilter;

  if (pathname === "/projects") {
    currentFilter = "/after-booking";
  } else if (["/before-booking", "/after-booking"].includes(pathname)) {
    currentFilter = pathname;
  } else {
    currentFilter = filterRoute || "/before-booking";
  }

  const isBeforeBooking = currentFilter === "/before-booking";

  // Esc closes the Add project pop-up
  useEffect(() => {
    if (!showAddModal) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") setShowAddModal(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showAddModal]);

  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

  const removeProjectNo = (name = "") => {
    return name.replace(/^\d{2}-\d{4}-\d{2}[_\s-]+/, "").trim();
  };

  // Splits "08-2026-01_DC-Name" into { code: "08-2026-01", label: "DC-Name" }
  const splitProjectName = (name = "") => {
    const match = name.match(/^(\d{2}-\d{4}-\d{2})[_\s-]+(.+)$/);

    if (match) {
      return { code: match[1], label: match[2].trim() };
    }

    return { code: "", label: name };
  };

  const filteredProjects = projects
    .filter((p) => {
      const hasPayment = !!p.hasPayment || Number(p.totalPaid || 0) >= 1;

      const lastUpdated = new Date(
        p.lastActivityAt ||
          p.lastUpdated ||
          p.updatedAt ||
          p.createdAt ||
          Date.now(),
      );

      const isInactive = lastUpdated < thirtyDaysAgo;

      const isClosed = p.isClosed === true;

      if (isBeforeBooking) {
        if (showArchived) {
          return !hasPayment && (p.isArchived || isInactive);
        }

        return !hasPayment && !p.isArchived && !isInactive;
      }

      if (currentFilter === "/after-booking") {
        if (showArchived) {
          return hasPayment && (isClosed || p.isArchived);
        }

        return hasPayment && !isClosed && !p.isArchived;
      }

      return false;
    })
    .filter((p) => p.name?.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const handleAddProject = () => {
    const trimmed = newProject.trim();

    if (trimmed === "") return;

    const exists = projects.some(
      (p) => removeProjectNo(p.name).toLowerCase() === trimmed.toLowerCase(),
    );

    if (exists) {
      alert("Project already exists");
      return;
    }

    const payload = {
      name: trimmed,
      address: "Not Provided",
    };

    onAddProject(payload);
    setNewProject("");
    setShowAddModal(false);
  };

  const handleProjectSelect = (project) => {
    onSelectProject(project, currentFilter);
    navigate("/project");
  };

  const isFinanceActive = pathname.toLowerCase().startsWith("/finance");
  const isReportActive = pathname.toLowerCase().startsWith("/payment-report");

  // ============================================================
  // FINANCE PERMISSION
  // Only these roles can see Finance:
  // admin, head_accountant, accountant
  // ============================================================

  let loggedInUser = {};

  try {
    loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch (error) {
    loggedInUser = {};
  }

  const userRole = String(
    loggedInUser?.role ||
      loggedInUser?.roleName ||
      localStorage.getItem("userRole") ||
      "",
  )
    .trim()
    .toLowerCase();

  const canViewFinance = ["admin", "head_accountant", "accountant"].includes(
    userRole,
  );

  // Payment Report is controlled from Role Management.
  const canViewPaymentReport = canAccess("paymentReport", "view");

  return (
    <div className="sb-root">
      {/* Top row: Back to Home + Add project */}
      <div className="sb-top-row">
        <button type="button" className="sb-back" onClick={() => navigate("/")}>
          <BackIcon />
          <span>Back</span>
        </button>

        {isBeforeBooking && !showArchived && (
          <button
            type="button"
            className="sb-add-open"
            onClick={() => setShowAddModal(true)}
          >
            <PlusIcon />
            <span>Add project</span>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="sb-search">
        <span className="sb-search-icon">
          <SearchIcon />
        </span>

        <input
          className="sb-search-input"
          type="text"
          placeholder="Search projects"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Finance - Admin / Head Accountant / Accountant Only */}
      {canViewFinance && (
        <button
          type="button"
          className={`sb-finance${isFinanceActive ? " active" : ""}`}
          onClick={() => navigate("/Finance")}
        >
          <WalletIcon />
          <span>Finance</span>
        </button>
      )}

      {/* Payment Report - role permission (Role Management) */}
      {canViewPaymentReport && (
        <button
          type="button"
          className={`sb-finance${isReportActive ? " active" : ""}`}
          onClick={() => navigate("/payment-report")}
        >
          <ReportIcon />
          <span>Payment Report</span>
        </button>
      )}

      {/* Heading + number of projects shown */}
      <div className="sb-head">
        <h2 className="sb-title">
          {showArchived
            ? isBeforeBooking
              ? "Archived projects"
              : "Closed / archived"
            : "Projects"}
        </h2>

        <span className="sb-count">{filteredProjects.length}</span>
      </div>

      {/* Active / Archived (or Closed) switch */}
      <div className="sb-segment">
        <button
          type="button"
          className={`sb-seg-btn${!showArchived ? " active" : ""}`}
          onClick={() => setShowArchived(false)}
        >
          Active
        </button>

        <button
          type="button"
          className={`sb-seg-btn${showArchived ? " active" : ""}`}
          onClick={() => setShowArchived(true)}
        >
          {isBeforeBooking ? "Archived" : "Closed"}
        </button>
      </div>

      {/* Project List */}
      <div className="sb-scroll">
        <ul className="sb-list">
          {filteredProjects.length > 0 ? (
            filteredProjects.map((project) => {
              const { code, label } = splitProjectName(project.name);
              const isActive = selectedProjectId === project._id;

              return (
                <li key={project._id}>
                  <button
                    type="button"
                    className={`sb-item${isActive ? " active" : ""}`}
                    onClick={() => handleProjectSelect(project)}
                    title={project.name}
                  >
                    {code && <span className="sb-item-code">{code}</span>}
                    <span className="sb-item-name">{label}</span>
                  </button>
                </li>
              );
            })
          ) : (
            <li className="sb-empty">No project found</li>
          )}
        </ul>
      </div>

      {/* Add project pop-up */}
      {showAddModal &&
        createPortal(
          <div
            className="sb-modal-overlay"
            onClick={() => setShowAddModal(false)}
          >
            <div
              className="sb-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Add project"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="sb-modal-title">New project</h3>
              <p className="sb-modal-sub">Enter the name of the new project.</p>

              <label className="sb-modal-label" htmlFor="sb-new-project">
                Project name
              </label>

              <input
                id="sb-new-project"
                className="sb-modal-input"
                type="text"
                autoFocus
                value={newProject}
                onChange={(e) => setNewProject(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddProject();
                }}
                placeholder="Enter project name"
              />

              <div className="sb-modal-actions">
                <button
                  type="button"
                  className="sb-modal-cancel"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="sb-modal-save"
                  onClick={handleAddProject}
                >
                  Add project
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Sidebar;
